import React from 'react';
import { Prompt, Redirect, useParams, useHistory } from 'react-router';
import { LayoutContext, AuthContext, ListingLabelContext } from 'contexts';
import { useBreakpoint, useSearchParams, useListing } from 'hooks';
import {
  TabPane,
  Page,
  JumpTo,
  Spin,
  Tabs,
  Modal,
  Button,
  message,
} from 'components';
import { isAuthForRole } from 'utils';
import { SaveListing, Visibility } from '../components';
import { StyledBlurOverlay } from '../StyledComponents';
import { useNavItems, useCards } from '../hooks';
import DetailsForm from '../forms/DetailsForm';
import StatusPriceChangeForm from '../forms/StatusPriceChangeForm';
import VisibilityForm from '../forms/VisibilityForm';

const EditListingPage: React.FC = () => {
  const history = useHistory();
  const { auth, hasLimitedAccess } = React.useContext(AuthContext);
  const { scrollContainer } = React.useContext(LayoutContext);
  const { getLabel } = React.useContext(ListingLabelContext);

  const { listingId } = useParams<{
    listingId: string;
  }>();
  const { submit } = useSearchParams();
  const { md, lg } = useBreakpoint();

  const [currentTab, setCurrentTab] = React.useState('details');

  const {
    listing,
    setListing,
    initialListing,
    isListingModified,
    isListingDeletedOrRestored,
    photos,
    setPhotos,
    setDeletedPhotos,
    thumbnail,
    setThumbnail,
    attachments,
    updateFile,
    loading,
    uploading,
    saveListing,
    deleteListing,
    restoreListing,
    submitListingForReview,
    errorMessage,
    formFieldsMissing,
    openHousesErrors,
    isFormValid,
    saved,
    validAvailabilities,
    availabilityDetails,
    setAvailabilityDetails,
    initialAvailabilityDetails,
    hasFeature,
    hasNarSettlement,
  } = useListing(listingId);

  const [agreedToTerms, setAgreedToTerms] = React.useState(false);
  const [submitModalOpen, setSubmitModalOpen] = React.useState(false);

  const handleTabChange = (newTab: string) => {
    const redirectMap = {
      appointmentsConfiguration: 'appointments/config',
      offerSettings: 'offer_settings',
      feedbackSettings: 'appointments/feedback_settings',
    };

    const redirectPath = redirectMap[newTab as keyof typeof redirectMap];

    if (redirectPath) {
      history.push(`/listing/${listingId}/${redirectPath}`);
      return;
    }
    setCurrentTab(newTab);
  };

  const renderTabPane = (label: string, key: string, dataCy?: string) => (
    <TabPane key={key} tab={<div data-cy={dataCy}>{label}</div>}>
      <></>
    </TabPane>
  );

  React.useEffect(() => {
    if (submit === 'true' && !auth.isAdmin && hasFeature('CREATE_LISTING')) {
      setSubmitModalOpen(true);
    }
  }, [submit, auth, hasFeature]);

  const cards = useCards(listing);
  const navItems = useNavItems({
    listing,
    photos,
    attachments,
    cards: [...cards],
    isFormValid,
  });

  const onSaveNewListing = React.useCallback(async () => {
    const isSaved = await saveListing();
    if (isSaved) {
      history.push(`/listing/${listingId}/view`);
    } else {
      scrollContainer?.scrollTo(0, 0);
    }
  }, [saveListing, listingId]);

  if (!auth || !listing.id) return null;

  if (
    !isAuthForRole({
      auth,
      locations: listing.locations,
      reps: listing.reps,
      // @ts-expect-error: wrong listing type
      hiddenReps: listing.hiddenReps,
    }) ||
    !listing.canEdit
  ) {
    message.error(
      'You do not have permissions to view this page. Please contact support@brokerbay.com for more information.',
    );
    return <Redirect to="/" />;
  }

  return (
    <>
      <Page
        overAlertVariant={hasLimitedAccess ? 'noAccess' : undefined}
        title={md ? 'Edit Listing' : null}
        showBackButton={md}
        type="hollow"
        fillScreenHeight
        headerExtra={
          <SaveListing
            agreedToTerms={agreedToTerms}
            listing={listing}
            listingId={listingId}
            loading={loading}
            onSaveNewListing={onSaveNewListing}
            deleteListing={deleteListing}
            saved={saved}
            isDeleted={listing.isDeleted}
            restoreListing={restoreListing}
            setSubmitModalOpen={setSubmitModalOpen}
            isBeingPublished={
              listing.visibility !== initialListing.visibility &&
              listing.visibility !== 'private'
            }
            editing
            photos={photos}
            attachments={attachments}
            uploading={uploading}
            hasLimitedAccess={hasLimitedAccess}
          />
        }
        subHeader={
          <Tabs
            onChange={handleTabChange}
            activeKey={currentTab}
            type="primary"
          >
            {renderTabPane('Details', 'details')}
            {renderTabPane(
              `${getLabel('Status')}/Price Change`,
              'statusPriceChange',
              'listing-edit-availability-price',
            )}
            {hasFeature('MY_BROKERAGE') &&
              renderTabPane(
                'Visibility',
                'visibility',
                'listing-edit-visibility',
              )}
            {renderTabPane(
              'Showing Configuration',
              'appointmentsConfiguration',
              'listing-edit-appt-config',
            )}
            {hasFeature('OFFERS') &&
              renderTabPane(
                'Offer Settings',
                'offerSettings',
                'listing-edit-offer-settings',
              )}
            {renderTabPane('Feedback Settings', 'feedbackSettings')}
          </Tabs>
        }
        asidePlacement="left"
        asideWidth={230}
        aside={
          !loading &&
          lg &&
          currentTab === 'details' && (
            <div>
              <JumpTo
                items={navItems}
                targetOffset={25}
                firstItemAlwaysActive
              />
            </div>
          )
        }
      >
        <Prompt
          when={
            isListingModified &&
            !saved &&
            !loading &&
            !isListingDeletedOrRestored
          }
          message="Are you sure you want to leave without saving?"
        />
        {loading ? (
          <Spin center size="large" />
        ) : (
          <StyledBlurOverlay $blur={hasLimitedAccess}>
            {currentTab === 'details' && (
              <DetailsForm
                listing={listing}
                setListing={setListing}
                photos={photos}
                setPhotos={setPhotos}
                setDeletedPhotos={setDeletedPhotos}
                thumbnail={thumbnail}
                setThumbnail={setThumbnail}
                errorMessage={errorMessage}
                formFieldsMissing={formFieldsMissing}
                openHousesErrors={openHousesErrors}
                attachments={attachments}
                updateFile={updateFile}
                uploading={uploading}
                editing
                hasNarSettlement={hasNarSettlement}
              />
            )}
            {currentTab === 'statusPriceChange' && (
              <StatusPriceChangeForm
                listing={listing}
                errorMessage={errorMessage}
                validAvailabilities={validAvailabilities}
                availabilityDetails={availabilityDetails}
                setAvailabilityDetails={setAvailabilityDetails}
                initialAvailabilityDetails={initialAvailabilityDetails}
                setListing={setListing}
              />
            )}
            {currentTab === 'visibility' && (
              <VisibilityForm
                listing={listing}
                initialListing={initialListing}
                setListing={setListing}
                thumbnail={thumbnail?.url}
                editing
                agreedToTerms={agreedToTerms}
                setAgreedToTerms={setAgreedToTerms}
              />
            )}
          </StyledBlurOverlay>
        )}
      </Page>
      {hasFeature('CREATE_LISTING') && (
        <Modal
          title="Submit For Review"
          open={submitModalOpen && listing.documentStatus === 'uploaded'}
          onCancel={() => setSubmitModalOpen(false)}
          destroyOnClose
          footer={
            <>
              <Button type="text" onClick={() => setSubmitModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setSubmitModalOpen(false);
                  onSaveNewListing();
                }}
              >
                Save
              </Button>
              <Button
                type="primary"
                disabled={
                  (!agreedToTerms && listing.pendingVisibility === 'network') ||
                  !listing.pendingVisibility
                }
                onClick={() => submitListingForReview()}
              >
                Save &amp; Submit
              </Button>
            </>
          }
        >
          <Visibility
            editing
            submitting
            thumbnail={thumbnail?.url}
            line1={listing.line1}
            line2={listing.line2}
            city={listing.city}
            province={listing.province}
            postal_code={listing.postal_code}
            list_price={listing.list_price}
            initialListing={initialListing}
            visibility={listing.visibility}
            pendingVisibility={listing.pendingVisibility}
            pageStatus={listing.pageStatus}
            status={listing.status}
            agreedToTerms={agreedToTerms}
            setListing={setListing}
            setAgreedToTerms={setAgreedToTerms}
          />
        </Modal>
      )}
    </>
  );
};

export default EditListingPage;
